<script setup lang="ts">
import { computed } from "vue"
import { useAuthStore } from "../stores/auth"
import {
  LayoutDashboard,
  Briefcase,
  ClipboardList,
  ShieldCheck,
  FileSearch,
  Server,
  Activity,
  Receipt,
  FlaskConical,
  Users,
  Smile,
  Crown,
  ScrollText,
  Settings,
  TrendingUp,
  Gem,
} from "lucide-vue-next"

const auth = useAuthStore()

interface NavItem {
  label: string
  to: string
  icon: any
  role: string
}
const all: NavItem[] = [
  { label: "Dashboard",            to: "/dashboard",       icon: LayoutDashboard, role: "mc-viewer" },
  { label: "Management",           to: "/m/management",    icon: Crown,           role: "mc-management" },
  { label: "BD & Marketing",       to: "/m/bd",            icon: Briefcase,       role: "mc-bd" },
  { label: "PMO — Studies",        to: "/m/pmo",           icon: ClipboardList,   role: "mc-pmo" },
  { label: "QM",                   to: "/m/qm",            icon: ShieldCheck,     role: "mc-qm" },
  { label: "QA",                   to: "/m/qa",            icon: FileSearch,      role: "mc-qa" },
  { label: "IT",                   to: "/m/it",            icon: Server,          role: "mc-it" },
  { label: "Operations",           to: "/m/ops",           icon: Activity,        role: "mc-ops" },
  { label: "Finance",              to: "/m/finance",       icon: Receipt,         role: "mc-finance" },
  { label: "Loyalty Shares",       to: "/m/loyalty-shares", icon: Gem,            role: "mc-shares" },
  { label: "R&D",                  to: "/m/rd",            icon: FlaskConical,    role: "mc-rd" },
  { label: "People & Culture",     to: "/m/pc",            icon: Users,           role: "mc-pc" },
  { label: "Customer Experience",  to: "/m/cx",            icon: Smile,           role: "mc-cx" },
  // Visible to every authenticated role (mc-viewer is the floor).
  { label: "RBQM Trends",          to: "/m/rbqm-trends",   icon: TrendingUp,      role: "mc-viewer" },
]

const adminItems: NavItem[] = [
  // mc-audit-explorer plugin: filterable timeline + counts. The legacy
  // /audit view still works (deprecated read-only list) but the explorer
  // supersedes it.
  { label: "Audit Explorer", to: "/m/admin/audit",    icon: ScrollText, role: "mc-admin" },
  { label: "Settings",       to: "/m/admin/settings", icon: Settings,   role: "mc-admin" },
]

const visibleNav    = computed(() => all.filter((i) => auth.hasRole(i.role) || auth.isAdmin || auth.isManagement))
const visibleAdmin  = computed(() => adminItems.filter((i) => auth.hasRole(i.role)))
</script>

<template>
  <aside class="w-60 bg-white border-r border-rule">
    <nav class="px-3 py-6 space-y-1">
      <div class="mc-kicker px-3 mb-2">MODULES</div>
      <RouterLink
        v-for="item in visibleNav" :key="item.to"
        :to="item.to"
        class="flex items-center gap-3 px-3 py-2 rounded-md text-sm text-ink hover:bg-canvas"
        active-class="bg-canvas font-semibold border-l-2 border-orange"
      >
        <component :is="item.icon" class="w-4 h-4" />
        {{ item.label }}
      </RouterLink>

      <template v-if="visibleAdmin.length > 0">
        <div class="mc-kicker px-3 mt-6 mb-2">ADMIN</div>
        <RouterLink
          v-for="item in visibleAdmin" :key="item.to"
          :to="item.to"
          class="flex items-center gap-3 px-3 py-2 rounded-md text-sm text-ink hover:bg-canvas"
          active-class="bg-canvas font-semibold border-l-2 border-orange"
        >
          <component :is="item.icon" class="w-4 h-4" />
          {{ item.label }}
        </RouterLink>
      </template>
    </nav>
  </aside>
</template>
